import { formatCurrency, formatDate } from '@eriko/shared-types';
import {
  AlertCircle,
  ArrowLeftRight,
  CheckCircle2,
  Clock,
  HelpCircle,
  ListChecks,
  Sparkles,
} from 'lucide-react';
import type { ComponentType } from 'react';

interface ReconciliationTransaction {
  transactionId: string;
  date: string;
  description: string;
  amount: string | number;
  type: 'CREDIT' | 'DEBIT' | string;
  reasonBadge?: string | null;
  flagReason?: string | null;
  currency?: string | null;
}

interface ReconciliationBucketSummary {
  count: number;
}

interface ReconciliationSummary {
  total: number;
  autoMatched: ReconciliationBucketSummary;
  suggested: ReconciliationBucketSummary;
  needsHelp: ReconciliationBucketSummary;
  transfers: ReconciliationBucketSummary;
  pendingReview: ReconciliationBucketSummary;
  multipleCandidates: ReconciliationBucketSummary;
}

interface ReconciliationListData {
  type: string;
  summary: ReconciliationSummary;
  groups: {
    suggested?: ReconciliationTransaction[];
    needsHelp?: ReconciliationTransaction[];
    autoMatched?: ReconciliationTransaction[];
    transfers?: ReconciliationTransaction[];
    multipleCandidates?: ReconciliationTransaction[];
  };
  currency?: string | null;
  period?: string | null;
  monthlyCloseId?: string | null;
}

type BucketKey =
  | 'autoMatched'
  | 'suggested'
  | 'needsHelp'
  | 'multipleCandidates'
  | 'transfers'
  | 'pendingReview';

interface BucketConfig {
  key: BucketKey;
  label: string;
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
  iconClass: string;
  groupKey?: keyof ReconciliationListData['groups'];
}

const BUCKETS: BucketConfig[] = [
  {
    key: 'autoMatched',
    label: 'Auto-matched',
    icon: CheckCircle2,
    iconClass: 'text-pos',
    groupKey: 'autoMatched',
  },
  {
    key: 'suggested',
    label: 'Suggested',
    icon: Sparkles,
    iconClass: 'text-info',
    groupKey: 'suggested',
  },
  {
    key: 'multipleCandidates',
    label: 'Multiple matches',
    icon: HelpCircle,
    iconClass: 'text-warn',
    groupKey: 'multipleCandidates',
  },
  {
    key: 'needsHelp',
    label: 'Needs help',
    icon: AlertCircle,
    iconClass: 'text-neg',
    groupKey: 'needsHelp',
  },
  {
    key: 'transfers',
    label: 'Transfers',
    icon: ArrowLeftRight,
    iconClass: 'text-surface-500',
    groupKey: 'transfers',
  },
  {
    key: 'pendingReview',
    label: 'Pending review',
    icon: Clock,
    iconClass: 'text-surface-500',
  },
];

function toNumber(amount: string | number): number {
  if (typeof amount === 'number') return amount;
  const parsed = Number(amount);
  return Number.isFinite(parsed) ? parsed : 0;
}

function TransactionRow({
  txn,
  fallbackCurrency,
}: {
  txn: ReconciliationTransaction;
  fallbackCurrency: string;
}) {
  const amount = toNumber(txn.amount);
  const isCredit = txn.type === 'CREDIT';
  const currency = txn.currency ?? fallbackCurrency;

  return (
    <div className="flex items-start gap-3 border-t border-surface-100 px-4.5 py-2.5 first:border-t-0">
      <div className="w-16 shrink-0 pt-0.5 text-[12px] tabular-nums text-surface-500">
        {formatDate(txn.date, 'MMM d')}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] text-surface-800">{txn.description}</div>
        {txn.reasonBadge && (
          <div className="mt-1 inline-flex items-center rounded-full bg-surface-50 px-2 py-0.5 text-[11px] font-medium text-surface-600">
            {txn.reasonBadge}
          </div>
        )}
      </div>
      <div
        className={`shrink-0 pt-0.5 text-right font-mono text-[13px] font-medium tabular-nums ${
          isCredit ? 'text-pos' : 'text-surface-900'
        }`}
      >
        {isCredit ? '+' : '−'}
        {formatCurrency(Math.abs(amount), 2, currency)}
      </div>
    </div>
  );
}

function BucketSection({
  bucket,
  count,
  transactions,
  fallbackCurrency,
}: {
  bucket: BucketConfig;
  count: number;
  transactions: ReconciliationTransaction[];
  fallbackCurrency: string;
}) {
  if (count === 0 && transactions.length === 0) return null;
  const Icon = bucket.icon;

  return (
    <div className="border-t border-surface-100">
      <div className="flex items-center justify-between bg-surface-25 px-4.5 py-2">
        <div className="flex items-center gap-2">
          <Icon className={`h-3.75 w-3.75 ${bucket.iconClass}`} strokeWidth={1.75} />
          <span className="text-[12px] font-semibold uppercase tracking-wide text-surface-600">
            {bucket.label}
          </span>
        </div>
        <span className="font-mono text-[12px] tabular-nums text-surface-500">{count}</span>
      </div>
      {transactions.length > 0 && (
        <div>
          {transactions.map((txn) => (
            <TransactionRow
              key={txn.transactionId}
              txn={txn}
              fallbackCurrency={fallbackCurrency}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function ReconciliationListReport({ data }: { data: Record<string, unknown> }) {
  const report = data as unknown as ReconciliationListData;
  const fallbackCurrency = report.currency ?? 'USD';
  const summary = report.summary;
  const total = summary?.total ?? 0;

  return (
    <div className="overflow-hidden rounded-[10px] border border-surface-200 bg-surface-0 transition-shadow hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-surface-100 px-4.5 py-3.5">
        <div className="flex items-center gap-2.5">
          <ListChecks className="h-4.25 w-4.25 text-info" strokeWidth={1.5} />
          <span className="text-sm font-semibold text-surface-900">Reconciliation</span>
        </div>
        <div className="flex items-center gap-2">
          {report.period && (
            <span className="rounded-full bg-surface-50 px-2.5 py-0.75 text-xs font-medium text-surface-500">
              {report.period}
            </span>
          )}
          <span className="rounded-full bg-surface-50 px-2.5 py-0.75 font-mono text-xs font-medium tabular-nums text-surface-500">
            {total} {total === 1 ? 'txn' : 'txns'}
          </span>
        </div>
      </div>

      {/* Buckets */}
      {BUCKETS.map((bucket) => {
        const count = summary?.[bucket.key]?.count ?? 0;
        const transactions = bucket.groupKey
          ? (report.groups?.[bucket.groupKey] ?? [])
          : [];
        return (
          <BucketSection
            key={bucket.key}
            bucket={bucket}
            count={count}
            transactions={transactions}
            fallbackCurrency={fallbackCurrency}
          />
        );
      })}
    </div>
  );
}
